/**
 * TreeNode — Recursive strategy node renderer
 *
 * Renders each node as a self-describing, color-coded block.
 * Handles collapse, selection, context menu, and "+" add buttons.
 */

import { memo, useState, useCallback } from 'react';
import clsx from 'clsx';
import { useBuilderStore, useSelectedNodeId } from '../../stores/builderStore';
import { NODE_COLORS, ALLOWED_CHILDREN, canHaveChildren, formatSignalInline, formatFilterSignalInline } from '../../types/builder';
import type { StrategyNode, NodeType } from '../../types/builder';
import { AddNodeMenu } from './AddNodeMenu';
import { BuilderContextMenu } from './ContextMenu';

interface TreeNodeProps {
  node: StrategyNode;
  depth: number;
  isRoot?: boolean;
  isLast?: boolean;
  parentWeights?: number[];
  childIndex?: number;
  branchLabel?: 'then' | 'else';
}

export const TreeNode = memo(({ node, depth, isRoot, isLast, parentWeights, childIndex, branchLabel }: TreeNodeProps) => {
  const selectedNodeId = useSelectedNodeId();
  const selectNode = useBuilderStore((s) => s.selectNode);
  const toggleCollapse = useBuilderStore((s) => s.toggleCollapse);

  const [ctxMenu, setCtxMenu] = useState<{ x: number; y: number } | null>(null);

  const isSelected = selectedNodeId === node.id;
  const colors = NODE_COLORS[node.type];
  const hasKids = canHaveChildren(node.type);
  const weight = parentWeights && childIndex !== undefined ? parentWeights[childIndex] : undefined;

  const handleContextMenu = useCallback((e: React.MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setCtxMenu({ x: e.clientX, y: e.clientY });
    selectNode(node.id);
  }, [node.id, selectNode]);

  const handleClick = useCallback(() => selectNode(node.id), [node.id, selectNode]);

  // ─── Inline display content ─────────────────────────────
  const renderContent = () => {
    switch (node.type) {
      case 'asset':
        return (
          <>
            <span className={clsx('text-xs font-bold px-1.5 py-0.5 rounded', colors.pill)}>●</span>
            <span className="text-sm font-semibold text-surface-100">{node.ticker || 'Select Asset'}</span>
            {node.name && <span className="text-xs text-surface-400 truncate max-w-[180px]">{node.name}</span>}
          </>
        );

      case 'group':
        return (
          <>
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded', colors.pill)}>BASKET</span>
            <span className="text-sm font-medium text-surface-200">{node.name}</span>
          </>
        );

      case 'weight-equal':
        return (
          <>
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded', colors.pill)}>EQUAL ALLOCATION</span>
            <span className="text-xs text-surface-400">
              {node.children.length} items · {(100 / Math.max(node.children.length, 1)).toFixed(1)}% each
            </span>
          </>
        );

      case 'weight-specified':
        return (
          <>
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded', colors.pill)}>CUSTOM ALLOCATION</span>
            <span className="text-xs text-surface-400">
              {node.children.length} items
            </span>
          </>
        );

      case 'weight-inverse-volatility':
        return (
          <>
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded', colors.pill)}>VOL WEIGHTED</span>
            <span className="text-xs text-surface-400">{node.window}d window · {node.children.length} items</span>
          </>
        );

      case 'if': {
        const condStr = `${formatSignalInline(node.condition.left)} ${node.condition.operator} ${
          typeof node.condition.right === 'number' ? node.condition.right : formatSignalInline(node.condition.right)
        }`;
        return (
          <>
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded', colors.pill)}>CONDITIONAL</span>
            <span className="text-xs text-surface-300 truncate max-w-[280px]">{condStr}</span>
          </>
        );
      }

      case 'filter':
        return (
          <>
            <span className={clsx('text-xs font-bold px-2 py-0.5 rounded', colors.pill)}>SELECTION</span>
            <span className="text-xs text-surface-300">
              {node.selector.direction} {node.selector.count} by {formatFilterSignalInline(node.signal)}
            </span>
          </>
        );
    }
  };

  // ─── Allowed children for the "+" button ────────────────
  const allowedTypes = ALLOWED_CHILDREN[node.type];
  // For 'if' and 'group' with special child structures, we only show "+" on their sub-containers
  const showAddButton = hasKids && allowedTypes.length > 0 && !node.collapsed;

  return (
    <div className="relative" style={{ marginLeft: isRoot ? 0 : 24 }}>
      {/* Vertical connector line from parent */}
      {!isRoot && (
        <div
          className="absolute left-[-12px] top-0 w-px bg-surface-600/50"
          style={{ height: isLast ? '16px' : '100%' }}
        />
      )}
      {/* Horizontal connector */}
      {!isRoot && <div className="absolute left-[-12px] top-[14px] w-3 h-px bg-surface-600/50" />}

      {/* Branch label (THEN / ELSE) */}
      {branchLabel && (
        <div className="flex items-center gap-1.5 mb-0.5">
          <span
            className={clsx(
              'text-[10px] font-bold px-1.5 py-0.5 rounded',
              branchLabel === 'then' ? 'bg-emerald-500/20 text-emerald-300' : 'bg-amber-500/20 text-amber-300',
            )}
          >
            {branchLabel === 'then' ? 'THEN' : 'ELSE'}
          </span>
        </div>
      )}

      {/* Node row */}
      <div
        className={clsx(
          'group flex items-center gap-2 py-1 px-2 cursor-pointer transition-all duration-150 border whitespace-nowrap',
          node.type === 'group' ? 'rounded-l-lg -mr-[9999px] pr-[9999px]' : 'rounded-lg',
          isSelected
            ? `${colors.bg} ${colors.border} ring-1 ring-primary-500/40`
            : `border-transparent hover:${colors.bg} hover:border-surface-700`,
        )}
        style={node.type === 'group' && !isSelected ? { background: 'rgba(168, 85, 247, 0.06)' } : undefined}
        onClick={handleClick}
        onContextMenu={handleContextMenu}
      >
        {/* Collapse toggle */}
        {hasKids ? (
          <button
            onClick={(e) => { e.stopPropagation(); toggleCollapse(node.id); }}
            className="w-4 h-4 flex items-center justify-center text-surface-400 hover:text-surface-200 transition-colors shrink-0"
          >
            <svg className={clsx('w-3 h-3 transition-transform', !node.collapsed && 'rotate-90')} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
            </svg>
          </button>
        ) : (
          <div className="w-4 shrink-0" />
        )}

        {/* Weight badge */}
        {weight !== undefined && (
          <span className="text-[10px] font-bold text-emerald-400 bg-emerald-500/15 px-1.5 py-0.5 rounded shrink-0">
            {(weight * 100).toFixed(weight * 100 % 1 === 0 ? 0 : 1)}%
          </span>
        )}

        {/* Node content */}
        {renderContent()}
      </div>

      {/* Children */}
      {!node.collapsed && (
        <div className="relative">
          {'children' in node && Array.isArray(node.children) && (
            <>
              {node.children.map((child, i) => (
                <TreeNode
                  key={child.id}
                  node={child}
                  depth={depth + 1}
                  isLast={i === node.children.length - 1 && !showAddButton}
                  parentWeights={node.type === 'weight-specified' ? node.weights : undefined}
                  childIndex={node.type === 'weight-specified' ? i : undefined}
                />
              ))}
              {showAddButton && (
                <div className="relative" style={{ marginLeft: 24 }}>
                  <div className="absolute left-[-12px] top-0 w-px bg-surface-600/50" style={{ height: '14px' }} />
                  <div className="absolute left-[-12px] top-[14px] w-3 h-px bg-surface-600/50" />
                  <AddNodeMenu parentId={node.id} allowedTypes={allowedTypes as NodeType[]} />
                </div>
              )}
            </>
          )}

          {'child' in node && node.child && (
            <TreeNode node={node.child} depth={depth + 1} isLast />
          )}

          {'thenBranch' in node && (
            <>
              <TreeNode node={node.thenBranch} depth={depth + 1} branchLabel="then" />
              <TreeNode node={node.elseBranch} depth={depth + 1} isLast branchLabel="else" />
            </>
          )}
        </div>
      )}

      {/* Collapsed count badge */}
      {node.collapsed && hasKids && (
        <div className="ml-10 py-0.5">
          <span className="text-[10px] text-surface-500 bg-surface-800 px-2 py-0.5 rounded-full">
            ··· collapsed
          </span>
        </div>
      )}

      {/* Context Menu */}
      {ctxMenu && (
        <BuilderContextMenu
          node={node}
          x={ctxMenu.x}
          y={ctxMenu.y}
          onClose={() => setCtxMenu(null)}
          isRoot={isRoot}
        />
      )}
    </div>
  );
});
TreeNode.displayName = 'TreeNode';
